import React, { useState, useEffect, useCallback } from 'react';
import MainLayout from '../components/MainLayout';
import axios from 'axios';
import { Calculator, RefreshCw, Download } from 'lucide-react';
import { useActiveCompany } from '../hooks/useActiveCompany';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const StatutoryWorking = () => {
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useActiveCompany();
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7));
  const [workingType, setWorkingType] = useState('pf');
  const [workingData, setWorkingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [state, setState] = useState('');

  const fetchCompanies = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/companies`, { withCredentials: true });
      setCompanies(data); setSelectedCompany((cur) => (data.length > 0 && !cur ? data[0].id : cur));
    } catch (e) { console.error(e); }
  }, [setSelectedCompany]);

  useEffect(() => { fetchCompanies(); }, [fetchCompanies]);


  const computeWorking = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/statutory/working/compute`, { company_id: selectedCompany, period, working_type: workingType, state: state || null }, { withCredentials: true });
      setWorkingData(data);
    } catch (e) { alert(e.response?.data?.detail || 'Error'); } finally { setLoading(false); }
  };


  const fetchWorking = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/statutory/working?company_id=${selectedCompany}&period=${period}&working_type=${workingType}`, { withCredentials: true });
      setWorkingData(data);
    } catch (e) {
      setWorkingData(null);
    }
  }, [period, selectedCompany, workingType]);

  useEffect(() => { if (selectedCompany) fetchWorking(); }, [selectedCompany, period, workingType, fetchWorking]);

  const workings = workingData?.workings || workingData?.employee_workings || [];

  const totals = {
    pf_ee: workings.reduce((s, w) => s + (w.pf?.employee_pf || 0), 0),
    pf_er: workings.reduce((s, w) => s + (w.pf?.employer_pf || 0), 0),
    eps: workings.reduce((s, w) => s + (w.pf?.eps_amount || 0), 0),
    edli: workings.reduce((s, w) => s + (w.pf?.edli_amount || 0), 0),
    esi_ee: workings.reduce((s, w) => s + (w.esi?.employee_esi || 0), 0),
    esi_er: workings.reduce((s, w) => s + (w.esi?.employer_esi || 0), 0),
    pt: workings.reduce((s, w) => s + (w.pt?.amount || 0), 0),
    lwf_ee: workings.reduce((s, w) => s + (w.lwf?.employee_amount || 0), 0),
    lwf_er: workings.reduce((s, w) => s + (w.lwf?.employer_amount || 0), 0),
  };

  return (
    <MainLayout>
      <div data-testid="statutory-working-page" className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Statutory Working</h1>
            <p className="text-sm text-slate-500 mt-1">PF, ESI, PT, LWF working sheets with employee-level detail</p>
          </div>
          <div className="flex items-center space-x-3">
            <select data-testid="company-selector" value={selectedCompany} onChange={(e) => setSelectedCompany(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>

        <div className="flex items-center space-x-3 bg-white rounded-xl border border-slate-200 p-4">
          <div><label className="block text-xs text-slate-500 mb-1">Period</label><input type="month" data-testid="period-input" value={period} onChange={(e) => setPeriod(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
          <div><label className="block text-xs text-slate-500 mb-1">Type</label>
            <select data-testid="working-type-select" value={workingType} onChange={(e) => setWorkingType(e.target.value)} className="border border-slate-300 rounded-lg px-3 py-2 text-sm">
              <option value="pf">PF / EPS / EDLI</option><option value="esi">ESI</option><option value="pt">Professional Tax</option><option value="lwf">Labour Welfare Fund</option><option value="gratuity">Gratuity</option><option value="bonus">Bonus</option><option value="leave_encashment">Leave Encashment</option><option value="all">All</option>
            </select></div>
          <div><label className="block text-xs text-slate-500 mb-1">State</label><input value={state} onChange={(e) => setState(e.target.value)} placeholder="All" className="border border-slate-300 rounded-lg px-3 py-2 text-sm w-32" /></div>
          <div className="pt-5"><button data-testid="compute-working-btn" onClick={computeWorking} disabled={loading} className="flex items-center space-x-2 bg-[#0052FF] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#0040CC] disabled:opacity-50"><RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /><span>Compute</span></button></div>
        </div>

        {workings.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
              <h2 className="text-base font-semibold text-slate-800">{workingType.toUpperCase()} Working - {period} ({workings.length} employees)</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <th className="px-3 py-2 text-left font-medium">Employee</th>
                    <th className="px-3 py-2 text-left font-medium">Code</th>
                    <th className="px-3 py-2 text-right font-medium">Basic</th>
                    <th className="px-3 py-2 text-right font-medium">Gross</th>
                    {(workingType === 'pf' || workingType === 'all') && <><th className="px-3 py-2 text-right font-medium">PF EE</th><th className="px-3 py-2 text-right font-medium">PF ER</th><th className="px-3 py-2 text-right font-medium">EPS</th><th className="px-3 py-2 text-right font-medium">EDLI</th></>}
                    {(workingType === 'esi' || workingType === 'all') && <><th className="px-3 py-2 text-right font-medium">ESI EE</th><th className="px-3 py-2 text-right font-medium">ESI ER</th></>}
                    {(workingType === 'pt' || workingType === 'all') && <th className="px-3 py-2 text-right font-medium">PT</th>}
                    {(workingType === 'lwf' || workingType === 'all') && <><th className="px-3 py-2 text-right font-medium">LWF EE</th><th className="px-3 py-2 text-right font-medium">LWF ER</th></>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {workings.map((w, i) => (
                    <tr key={i} className="hover:bg-slate-50">
                      <td className="px-3 py-2 text-slate-800 font-medium">{w.employee_name}</td>
                      <td className="px-3 py-2 text-slate-500 font-mono">{w.employee_code}</td>
                      <td className="px-3 py-2 text-right font-mono">{w.basic?.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono">{w.gross_wages?.toLocaleString()}</td>
                      {(workingType === 'pf' || workingType === 'all') && <>
                        <td className="px-3 py-2 text-right font-mono">{w.pf?.employee_pf?.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right font-mono">{w.pf?.employer_pf?.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right font-mono">{w.pf?.eps_amount?.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right font-mono">{w.pf?.edli_amount?.toLocaleString()}</td>
                      </>}
                      {(workingType === 'esi' || workingType === 'all') && <>
                        <td className="px-3 py-2 text-right font-mono">{w.esi?.employee_esi?.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right font-mono">{w.esi?.employer_esi?.toLocaleString()}</td>
                      </>}
                      {(workingType === 'pt' || workingType === 'all') && <td className="px-3 py-2 text-right font-mono">{w.pt?.amount?.toLocaleString()}</td>}
                      {(workingType === 'lwf' || workingType === 'all') && <>
                        <td className="px-3 py-2 text-right font-mono">{w.lwf?.employee_amount?.toLocaleString()}</td>
                        <td className="px-3 py-2 text-right font-mono">{w.lwf?.employer_amount?.toLocaleString()}</td>
                      </>}
                    </tr>
                  ))}
                  <tr className="bg-slate-50 font-semibold">
                    <td className="px-3 py-2" colSpan={2}>TOTAL</td>
                    <td className="px-3 py-2 text-right font-mono">{workings.reduce((s, w) => s + (w.basic || 0), 0).toLocaleString()}</td>
                    <td className="px-3 py-2 text-right font-mono">{workings.reduce((s, w) => s + (w.gross_wages || 0), 0).toLocaleString()}</td>
                    {(workingType === 'pf' || workingType === 'all') && <>
                      <td className="px-3 py-2 text-right font-mono">{totals.pf_ee.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono">{totals.pf_er.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono">{totals.eps.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono">{totals.edli.toLocaleString()}</td>
                    </>}
                    {(workingType === 'esi' || workingType === 'all') && <>
                      <td className="px-3 py-2 text-right font-mono">{totals.esi_ee.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono">{totals.esi_er.toLocaleString()}</td>
                    </>}
                    {(workingType === 'pt' || workingType === 'all') && <td className="px-3 py-2 text-right font-mono">{totals.pt.toLocaleString()}</td>}
                    {(workingType === 'lwf' || workingType === 'all') && <>
                      <td className="px-3 py-2 text-right font-mono">{totals.lwf_ee.toLocaleString()}</td>
                      <td className="px-3 py-2 text-right font-mono">{totals.lwf_er.toLocaleString()}</td>
                    </>}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {workings.length === 0 && (
          <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
            <Calculator className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-base font-semibold text-slate-600">No Working Data</h3>
            <p className="text-sm text-slate-400 mt-1">Select period and type, then click Compute to generate statutory working</p>
          </div>
        )}
      </div>
    </MainLayout>
  );
};

export default StatutoryWorking;
